import React, { useEffect, useMemo, useRef } from 'react';

import { BarChart, LineChart } from 'echarts/charts';
import { AriaComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { init, use as registerECharts } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { buildChartOption } from './charts';
import type { Cadence, ChartData } from './model';

registerECharts([BarChart, LineChart, AriaComponent, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

type Props = {
  title: string;
  data: ChartData;
  cadence: Cadence;
  caption: string;
  height?: number;
  truncated?: boolean;
  onCsv: () => void;
};

export default function AgentHubBdmChart({ title, data, cadence, caption, height = 300, truncated, onCsv }: Props) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const dark = theme.palette.mode === 'dark';
  const element = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof init> | undefined>(undefined);
  const option = useMemo(() => buildChartOption(data, cadence, dark, height), [data, cadence, dark, height]);
  useEffect(() => {
    if (!element.current) {
      return undefined;
    }
    const chart = init(element.current, undefined, { renderer: 'canvas' });
    instance.current = chart;
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(element.current);
    return () => {
      observer.disconnect();
      chart.dispose();
      instance.current = undefined;
    };
  }, []);
  useEffect(() => {
    instance.current?.setOption(option, { notMerge: true });
  }, [option]);
  const empty = data.categories.length === 0;
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', minWidth: 0, height: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1, bgcolor: tokens.plateHover, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Typography component="h2" variant="h6" sx={{ fontSize: 16 }}>
          {title}
        </Typography>
        <Button size="small" onClick={onCsv} disabled={empty}>
          CSV
        </Button>
      </Stack>
      {truncated && (
        <Alert severity="warning" sx={{ m: 1 }}>
          This chart reached its row limit, so some periods are missing. Choose a coarser cadence.
        </Alert>
      )}
      {empty && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 2 }}>
          No rows match these filters.
        </Typography>
      )}
      <Box
        ref={element}
        role="img"
        aria-label={`${title}. ${caption}`}
        sx={{ width: '100%', height, minWidth: 0, display: empty ? 'none' : 'block' }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', px: 2, pb: 1.5, overflowWrap: 'anywhere' }}
      >
        {caption}
      </Typography>
    </Paper>
  );
}
